import {Injectable, Type} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {EventApiModel} from '../../../../../api/models/event.api.model';
import {FollowApiModel} from '../../../../../api/models/follow.api-model';
import {QueryParamsApiModel} from '../../../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../../../api/models/result-list.api.model';
import {FollowerListItemInterface} from '../../../../../core/interfaces/follower-item.interface';
import {EventRestService} from '../../../../../api/services/event.rest.service';
import {SocialListAdapter, SocialListLoadResult} from '../social-list-adapter.interface';
import {SocialListModalConfig} from '../social-list-modal-config.interface';
import {SocialListType} from '../social-list-type.type';
import {FollowListItemComponent} from '../items/follow-list-item.component';
import {FollowListItemsBuilderService} from '../builders/follow-list-items-builder.service';

@Injectable({providedIn: 'root'})
export class EventFollowsSocialListAdapter implements SocialListAdapter<EventApiModel, never, FollowerListItemInterface> {
  public readonly type: SocialListType = 'event-follows';
  public readonly itemComponent: Type<FollowListItemComponent> = FollowListItemComponent;
  public readonly emptyTranslationKey = 'shared.modal.follow_list.empty';
  public readonly trimmedTranslationKey = 'shared.modal.follow_list.trimmed';
  public readonly titleTranslationKey = 'shared.modal.modal_service.follows.title';

  constructor(
    private readonly eventRS: EventRestService,
    private readonly followListItemsBuilder: FollowListItemsBuilderService,
  ) {
  }

  public loadPage(
    config: SocialListModalConfig<EventApiModel>,
    page: number,
    pageSize: number
  ): Observable<SocialListLoadResult<FollowerListItemInterface>> {
    const initialFollows = config.initialData || [];
    if (initialFollows.length) {
      const start = (page - 1) * pageSize;
      const records = initialFollows.slice(start, start + pageSize);

      return this.followListItemsBuilder.buildItems$(records, config.subject as any).pipe(
        map((items) => ({
          items,
          hasMore: start + records.length < initialFollows.length
        }))
      );
    }

    const params = new QueryParamsApiModel();
    params.page = page;
    params.limit = pageSize;
    params.pagination = true;

    return this.eventRS.getReceivedFollowsSubResourceCollection(config.subject as EventApiModel, params).pipe(
      map((resultListAM: ResultListApiModel<FollowApiModel>) => resultListAM?.records || []),
      mergeMap((followAMs) => this.followListItemsBuilder.buildItems$(followAMs, config.subject as any).pipe(
        map((items) => ({items, hasMore: followAMs.length >= pageSize}))
      )),
      catchError(() => of({items: [], hasMore: false}))
    );
  }

  public mergeItems(existingItems: FollowerListItemInterface[], incomingItems: FollowerListItemInterface[]): FollowerListItemInterface[] {
    return this.followListItemsBuilder.mergeItems(existingItems, incomingItems);
  }

  public trackBy(index: number, item: FollowerListItemInterface): string {
    return item?.follower?.['@id'] || `${index}`;
  }
}
